// src/components/orders/wizard/SummaryStep.js
import React from 'react';
import {
  ShoppingCart, ArrowLeft, Send, User, CreditCard, Package, Calculator, WifiOff, Wifi, MessageSquare
} from 'lucide-react';
import ApiService from '../../../services/api';

const SummaryStep = ({ 
  data, 
  updateData,
  validateStep, 
  validations, 
  prevStep, 
  onSuccess,
  loading,
  setLoading,
  isOnline,
  handleOfflineOrderSave,
  editingDraftId,
  clearDraft
}) => {

  // Converte il valore numerico dell'IVA nella stringa richiesta dal backend
  const convertVatRate = (vatNumber) => {
    switch (vatNumber) {
      case 4: return 'FOUR';
      case 10: return 'TEN';
      case 22: return 'TWENTYTWO';
      default: return 'TWENTYTWO'; // Default a 22%
    }
  };

  const calculateOrderTotals = () => {
    let subtotal = 0;
    let vat4Amount = 0;
    let vat10Amount = 0;
    let vat22Amount = 0;

    data.orderItems?.forEach((item) => {
      const lineTotal = item.quantita * item.prezzo;
      subtotal += lineTotal;

      const vatAmount = (lineTotal * item.iva) / 100;
      switch (item.iva) {
        case 4: vat4Amount += vatAmount; break;
        case 10: vat10Amount += vatAmount; break;
        case 22: vat22Amount += vatAmount; break;
        default: break;
      }
    });

    // Calcolo sconto in base al metodo di pagamento
    let discount = 0;
    switch (data.paymentMethod) {
      case 'consegna': discount = subtotal * 0.02; break;  // 2%
      case 'anticipato': discount = subtotal * 0.03; break; // 3%
      default: discount = 0; break; // contrassegno
    }

    const discountedSubtotal = subtotal - discount;
    const totalAmount = discountedSubtotal + vat4Amount + vat10Amount + vat22Amount;

    return {
      subtotal,
      discount,
      discountedSubtotal,
      vat4Amount,
      vat10Amount,
      vat22Amount,
      totalAmount
    };
  };

  const submitOrder = async () => {
    if (!data.customer?.companyName || !data.orderItems?.length) return;

    setLoading(true);

    try {
      const calculations = calculateOrderTotals();

      if (!isOnline) {
        // MODALITÀ OFFLINE
        
        const offlineOrderData = {
          customer: data.customer,
          paymentMethod: data.paymentMethod,
          discount: calculations.discount,
          subtotal: calculations.subtotal,
          totalAmount: calculations.totalAmount,
          items: data.orderItems.map((item) => ({
            product: item.product,
            productId: item.product?.id,
            quantity: item.quantita,
            unitPrice: item.prezzo,
            vatRate: convertVatRate(item.iva),
            description: item.descrizione,
            code: item.codice
          })),
          timestamp: Date.now(),
          offlineCreated: true
        };

        // Aggiungi destinationId se è stata selezionata una destinazione diversa dalla sede legale
        if (data.selectedDestination && data.selectedDestination.index !== 'main') {
          offlineOrderData.destinationId = data.selectedDestination.id;
        }

        // Aggiungi note se presenti
        if (data.notes && data.notes.trim()) {
          offlineOrderData.notes = data.notes.trim();
        }

        const result = await handleOfflineOrderSave(offlineOrderData);
        
        if (result.success) {
          clearDraft();
          onSuccess();
        } else {
          throw new Error(result.message || 'Errore salvataggio offline');
        }
      } else {
        // MODALITÀ ONLINE        
        const customerData = await ApiService.createCustomer(data.customer);

        const orderData = {
          customerId: customerData.customer.id,
          paymentMethod: data.paymentMethod,
          discount: calculations.discount,
          items: data.orderItems.map((item) => ({
            productId: item.product.id,
            quantity: item.quantita,
            unitPrice: item.prezzo,
            vatRate: convertVatRate(item.iva),
          })),
        };

        // Aggiungi destinationId se è stata selezionata una destinazione diversa dalla sede legale
        if (data.selectedDestination && data.selectedDestination.index !== 'main') {
          orderData.destinationId = data.selectedDestination.id;
        }

        // Aggiungi note se presenti
        if (data.notes && data.notes.trim()) {
          orderData.notes = data.notes.trim();
        }

        if (editingDraftId) {
          // Aggiorna ordine esistente
          await ApiService.updateOrder(editingDraftId, orderData);
          await ApiService.finalizeOrder(editingDraftId);
        } else {
          // Crea nuovo ordine
          const order = await ApiService.createOrder(orderData);
          await ApiService.finalizeOrder(order.id);
        }

        clearDraft();
        onSuccess();
      }
    } catch (error) {
      console.error('Errore:', error);
      alert('Errore nel ' + (editingDraftId ? 'aggiornamento' : 'creazione') + ' ordine: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const getPaymentMethodName = () => {
    switch (data.paymentMethod) {
      case 'contrassegno': return 'Contrassegno';
      case 'consegna': return 'Alla Consegna (sconto 2%)';
      case 'anticipato': return 'Anticipato (sconto 3%)';
      default: return data.paymentMethod;
    }
  };

  const calculations = calculateOrderTotals();
  const isValid = validations.final(data);

  return (
    <div className="p-4 sm:p-6 lg:p-8">
      <div className="text-center mb-6 lg:mb-8">
        <div className="w-16 h-16 lg:w-20 lg:h-20 rounded-full flex items-center justify-center mx-auto mb-4" style={{backgroundColor: '#fcfbd6'}}>
          <ShoppingCart className="w-8 h-8 lg:w-10 lg:h-10" style={{color: '#c48f75'}} />
        </div>
        <h2 className="text-xl lg:text-2xl font-bold text-gray-900 mb-2">
          Riepilogo Finale
        </h2>
        <p className="text-gray-600 text-sm lg:text-base">
          Verifica tutti i dettagli prima di finalizzare l'ordine
        </p>
        
        {/* Indicatore stato connessione */}
        <div className={`inline-flex items-center space-x-2 mt-3 px-3 py-1 rounded-full text-xs font-medium ${
          isOnline 
            ? 'text-gray-700'
            : 'bg-orange-100 text-orange-700'
        }`}
        style={isOnline ? {backgroundColor: '#fcfbd6'} : {}}>
          {isOnline ? <Wifi className="w-3 h-3" /> : <WifiOff className="w-3 h-3" />}
          <span>{isOnline ? 'Online - Ordine inviato immediatamente' : 'Offline - Ordine salvato per invio successivo'}</span>
        </div>
      </div>

      <div className="max-w-4xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Colonna Sinistra - Cliente e Pagamento */}
        <div className="space-y-6">
          {/* Dati Cliente */}
          <div className="rounded-xl p-6" style={{backgroundColor: '#fcfbd6'}}>
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
              <User className="w-5 h-5 mr-2" style={{color: '#c48f75'}} />
              Dati Cliente
            </h3>
            <div className="space-y-3">
              <div>
                <label className="text-sm font-medium text-gray-600">Ragione Sociale</label>
                <p className="text-gray-900 font-medium">{data.customer?.companyName}</p>
              </div>
              {data.customer?.city && (
                <div>
                  <label className="text-sm font-medium text-gray-600">Città</label>
                  <p className="text-gray-900">{data.customer.city}</p>
                </div>
              )}
              {data.customer?.vatNumber && (
                <div>
                  <label className="text-sm font-medium text-gray-600">P.IVA</label>
                  <p className="text-gray-900">{data.customer.vatNumber}</p>
                </div>
              )}
            </div>
          </div>

          {/* Modalità Pagamento */}
          <div className="bg-orange-50 rounded-xl p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
              <CreditCard className="w-5 h-5 mr-2 text-orange-600" />
              Modalità di Pagamento
            </h3>
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-gray-700">Metodo selezionato:</span>
                <span className="font-medium text-gray-900">
                  {getPaymentMethodName()}
                </span>
              </div>
              {calculations.discount > 0 && (
                <div className="flex items-center justify-between text-gray-700">
                  <span>Sconto applicato:</span>
                  <span className="font-medium">
                    -€{calculations.discount.toFixed(2)}
                  </span>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Colonna Destra - Articoli e Totali */}
        <div className="space-y-6">
          {/* Lista Articoli */}
          <div className="bg-white border border-gray-200 rounded-xl p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
              <Package className="w-5 h-5 mr-2" style={{color: '#c48f75'}} />
              Articoli Ordinati ({data.orderItems?.length || 0})
            </h3>
            <div className="space-y-3 max-h-64 overflow-y-auto">
              {data.orderItems?.map((item) => (
                <div key={item.id} className="flex justify-between items-center py-2 border-b border-gray-100 last:border-b-0">
                  <div className="flex-1">
                    <p className="font-medium text-gray-900">{item.descrizione}</p>
                    <p className="text-sm text-gray-600">
                      {item.codice} • {item.quantita} x €{item.prezzo.toFixed(2)}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="font-medium text-gray-900">€{item.imponibile.toFixed(2)}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Totali Finali */}
          <div className="rounded-xl p-6" style={{backgroundColor: '#fcfbd6'}}>
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
              <Calculator className="w-5 h-5 mr-2" style={{color: '#c48f75'}} />
              Riepilogo Totali
            </h3>

            <div className="space-y-3">
              <div className="flex justify-between">
                <span className="text-gray-700">Subtotale:</span>
                <span className="font-medium">€{calculations.subtotal.toFixed(2)}</span>
              </div>

              {calculations.discount > 0 && (
                <div className="flex justify-between text-gray-700">
                  <span>Sconto pagamento:</span>
                  <span className="font-medium">-€{calculations.discount.toFixed(2)}</span>
                </div>
              )}

              <div className="flex justify-between">
                <span className="text-gray-700">Subtotale scontato:</span>
                <span className="font-medium">€{calculations.discountedSubtotal.toFixed(2)}</span>
              </div>

              {calculations.vat4Amount > 0 && (
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">IVA 4%:</span>
                  <span>€{calculations.vat4Amount.toFixed(2)}</span>
                </div>
              )}

              {calculations.vat10Amount > 0 && (
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">IVA 10%:</span>
                  <span>€{calculations.vat10Amount.toFixed(2)}</span>
                </div>
              )}

              {calculations.vat22Amount > 0 && (
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">IVA 22%:</span>
                  <span>€{calculations.vat22Amount.toFixed(2)}</span>
                </div>
              )}

              <div className="border-t border-gray-300 pt-3">
                <div className="flex justify-between text-xl font-bold">
                  <span>TOTALE FINALE:</span>
                  <span style={{color: '#c48f75'}}>€{calculations.totalAmount.toFixed(2)}</span>
                </div>
                {calculations.discount > 0 && (
                  <div className="text-right">
                    <div className="text-sm text-gray-700 font-medium mt-1">
                      Risparmiato: €{calculations.discount.toFixed(2)}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Sezione Note */}
      <div className="max-w-4xl mx-auto mt-8">
        <div className="bg-gray-50 rounded-xl p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
            <MessageSquare className="w-5 h-5 mr-2 text-gray-600" />
            Note Aggiuntive (Opzionale)
          </h3>
          <textarea
            value={data.notes || ''}
            onChange={(e) => updateData({ notes: e.target.value })}
            placeholder="Aggiungi note per questo ordine..."
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 resize-none login-input"
            rows={3}
          />
          <p className="text-xs text-gray-500 mt-2">
            Le note verranno incluse nell'ordine e saranno visibili nei dettagli
          </p>
        </div>
      </div>

      {/* Navigation */}
      <div className="flex justify-between items-center mt-8 pt-6 border-t border-gray-200">
        <button
          onClick={prevStep}
          className="flex items-center space-x-2 border border-gray-300 text-gray-700 px-4 sm:px-6 lg:px-8 py-3 rounded-xl font-medium hover:bg-gray-50 transition-colors"
        >
          <ArrowLeft className="w-5 h-5" />
          <span>Indietro</span>
        </button>

        <button
          onClick={submitOrder}
          disabled={loading || !isValid}
          className={`flex items-center space-x-2 px-4 sm:px-6 lg:px-8 py-3 rounded-xl font-medium transition-all ${
            isValid && !loading
              ? 'text-white shadow-lg'
              : 'bg-gray-300 text-gray-500 cursor-not-allowed'
          }`}
          style={isValid && !loading ? {backgroundColor: '#c48f75'} : {}}
          onMouseEnter={(e) => (isValid && !loading) && (e.target.style.backgroundColor = '#4c4849')}
          onMouseLeave={(e) => (isValid && !loading) && (e.target.style.backgroundColor = '#c48f75')}
        >
          {loading ? (
            <>
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
              <span>{editingDraftId ? 'Aggiornamento...' : 'Finalizzazione...'}</span>
            </>
          ) : (
            <>
              {isOnline ? <Send className="w-5 h-5" /> : <WifiOff className="w-5 h-5" />}
              <span>
                {isOnline ? 
                  (editingDraftId ? 'Aggiorna Ordine' : 'Finalizza Ordine') : 
                  'Salva Offline'
                }
              </span>
            </>
          )}
        </button>
      </div>
    </div>
  );
};

export default SummaryStep;